import { apiGet, apiGetResponse, apiPost, apiPut } from '../lib/api-client';
import type {
  CourseListResponse,
  CourseDetail,
  CourseCategory,
  MyCourse,
  LessonDetail,
  QuizPayload,
  QuizSubmissionResult,
  QuizStartResponse,
  QuizAttemptDetail,
  Certificate,
  CertificateVerify,
  EnrollmentStatus,
} from '@edytech/shared/types';

export interface CourseQueryParams {
  category?: string;
  search?: string;
  level?: string;
  sort?: 'newest' | 'price_asc' | 'price_desc' | 'popular' | 'rating' | 'title_asc';
  page?: number;
  limit?: number;
}

export interface LessonProgressUpdate {
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
  positionSec: number;
}

export const courseService = {
  async getCategories(): Promise<CourseCategory[]> {
    return apiGet<CourseCategory[]>('/courses/categories');
  },

  async getCourses(params?: CourseQueryParams): Promise<CourseListResponse> {
    const query = new URLSearchParams();
    if (params?.category) query.append('category', params.category);
    if (params?.search) query.append('search', params.search);
    if (params?.level) query.append('level', params.level);
    if (params?.sort) query.append('sort', params.sort);
    if (params?.page) query.append('page', String(params.page));
    if (params?.limit) query.append('limit', String(params.limit));
    const qs = query.toString();
    // The API returns `{ success, data: Course[], pagination }` at the top level;
    // use the raw response so callers get both the items and pagination info.
    return apiGetResponse<CourseListResponse>(`/courses${qs ? `?${qs}` : ''}`);
  },

  async getCourseBySlug(slug: string): Promise<CourseDetail> {
    return apiGet<CourseDetail>(`/courses/${slug}`);
  },

  async getMyCourses(): Promise<MyCourse[]> {
    return apiGet<MyCourse[]>('/courses/my');
  },

  async enroll(courseId: number): Promise<{ id: number; status: string }> {
    return apiPost<{ id: number; status: string }>(`/courses/${courseId}/enroll`);
  },

  async getEnrollmentStatus(courseId: number): Promise<EnrollmentStatus | null> {
    const data = await apiGet<Record<string, unknown> | null>(`/courses/${courseId}/enrollment`);
    return data ? (data as { status: string }).status as EnrollmentStatus : null;
  },

  async getLesson(lessonId: number): Promise<LessonDetail> {
    return apiGet<LessonDetail>(`/lessons/${lessonId}`);
  },

  async saveProgress(lessonId: number, data: LessonProgressUpdate): Promise<Record<string, unknown>> {
    return apiPut<Record<string, unknown>>(`/lessons/${lessonId}/progress`, data);
  },

  async getQuiz(courseId: number): Promise<QuizPayload> {
    return apiGet<QuizPayload>(`/quizzes/course/${courseId}`);
  },

  /** Server-authoritative T-start: creates (or resumes) a timed attempt. */
  async startQuizAttempt(courseId: number): Promise<QuizStartResponse> {
    return apiPost<QuizStartResponse>(`/quizzes/course/${courseId}/attempt`);
  },

  /** Submit a specific attempt (idempotent by attemptId; server rejects after expiry). */
  async submitQuizAttempt(
    courseId: number,
    attemptId: number,
    answers: Record<string, number | number[]>
  ): Promise<QuizSubmissionResult> {
    return apiPost<QuizSubmissionResult>(`/quizzes/course/${courseId}/attempt/${attemptId}/submit`, { answers });
  },

  /** Result review for a specific attempt. */
  async getQuizAttempt(courseId: number, attemptId: number): Promise<QuizAttemptDetail> {
    return apiGet<QuizAttemptDetail>(`/quizzes/course/${courseId}/attempt/${attemptId}`);
  },

  async getCertificates(): Promise<Certificate[]> {
    return apiGet<Certificate[]>('/certificates');
  },

  async verifyCertificate(serial: string): Promise<CertificateVerify> {
    return apiGet<CertificateVerify>(`/certificates/${serial}/verify`);
  },
};

export type { Course, CourseDetail, MyCourse, LessonDetail, CourseCategory } from '@edytech/shared/types';